(function newsletterModule(globalScope) {
  'use strict';

  const DAY = 24 * 60 * 60 * 1000;
  const DISMISSED_KEY = 'wushu_newsletter_dismissed_at_v1';
  const SUBSCRIBED_KEY = 'wushu_newsletter_subscribed_at_v1';

  function createNewsletterState(storage, options = {}) {
    const memory = new Map();
    const dismissedFor = (options.dismissedDays || 7) * DAY;
    const subscribedFor = (options.subscribedDays || 90) * DAY;

    function readRaw(key) {
      try {
        const value = storage && storage.getItem(key);
        return value === null || value === undefined ? memory.get(key) || null : value;
      } catch (_error) {
        return memory.get(key) || null;
      }
    }

    function writeRaw(key, value) {
      memory.set(key, String(value));
      try {
        if (storage) storage.setItem(key, String(value));
      } catch (_error) {
        // Browser privacy settings may block storage; memory still prevents repeats this visit.
      }
    }

    function removeRaw(key) {
      memory.delete(key);
      try {
        if (storage) storage.removeItem(key);
      } catch (_error) {
        // Storage failure must never prevent newsletter use.
      }
    }

    function isActive(key, duration, now) {
      const raw = readRaw(key);
      if (raw === null) return false;

      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp) || timestamp <= 0 || timestamp > now) {
        removeRaw(key);
        return false;
      }

      if (now - timestamp >= duration) {
        removeRaw(key);
        return false;
      }

      return true;
    }

    function read(now = Date.now()) {
      const subscribed = isActive(SUBSCRIBED_KEY, subscribedFor, now);
      return {
        dismissed: subscribed ? false : isActive(DISMISSED_KEY, dismissedFor, now),
        subscribed,
      };
    }

    return {
      read,
      dismiss(now = Date.now()) {
        writeRaw(DISMISSED_KEY, now);
      },
      subscribe(now = Date.now()) {
        writeRaw(SUBSCRIBED_KEY, now);
        removeRaw(DISMISSED_KEY);
      },
      shouldAutoOpen(now = Date.now()) {
        const current = read(now);
        return !current.dismissed && !current.subscribed;
      },
    };
  }

  function createNewsletterController(options) {
    const state = options.state;
    const delay = Number.isFinite(options.delay) ? options.delay : 5000;
    const now = options.now || Date.now;
    const schedule = options.schedule || setTimeout;
    const cancelSchedule = options.cancelSchedule || clearTimeout;
    const onOpen = options.onOpen || function noop() {};
    const onClose = options.onClose || function noop() {};
    const onSubscribed = options.onSubscribed || function noop() {};
    let timer = null;

    function cancelPending() {
      if (timer === null) return;
      cancelSchedule(timer);
      timer = null;
    }

    return {
      start() {
        if (!state.shouldAutoOpen(now())) return;
        timer = schedule(() => {
          timer = null;
          onOpen();
        }, delay);
      },
      open() {
        cancelPending();
        onOpen();
      },
      close() {
        cancelPending();
        state.dismiss(now());
        onClose();
      },
      subscribe() {
        cancelPending();
        state.subscribe(now());
        onSubscribed();
      },
      destroy() {
        cancelPending();
      },
    };
  }

  function initNewsletter(root, dependencies = {}) {
    if (!root) return null;

    const documentRef = dependencies.document || root.ownerDocument;
    const storage = dependencies.storage || (globalScope && globalScope.localStorage);
    const dialog = root.querySelector('[data-newsletter-dialog]');
    const launcher = root.querySelector('[data-newsletter-launcher]');
    const closeButton = root.querySelector('[data-newsletter-close]');
    const backdrop = root.querySelector('[data-newsletter-backdrop]');
    const permanentForm = root.querySelector('[data-newsletter-permanent-form]');
    const thankYou = root.querySelector('[data-newsletter-thank-you]');
    const success = root.querySelector('[data-newsletter-success]');
    const delay = Number(root.dataset.delay) || 5000;
    const state = createNewsletterState(storage, {
      dismissedDays: Number(root.dataset.dismissedDays) || 7,
      subscribedDays: Number(root.dataset.subscribedDays) || 90,
    });
    let previouslyFocused = null;

    function showDialog() {
      if (!dialog) return;
      previouslyFocused = documentRef && documentRef.activeElement;
      dialog.hidden = false;
      if (closeButton && closeButton.focus) closeButton.focus();
    }

    function hideDialog() {
      if (dialog) dialog.hidden = true;
      const focusTarget = previouslyFocused || launcher;
      if (focusTarget && focusTarget.focus) focusTarget.focus();
      previouslyFocused = null;
    }

    function showSubscribedState() {
      if (dialog) dialog.hidden = true;
      if (launcher) launcher.hidden = true;
      if (permanentForm) permanentForm.hidden = true;
      if (thankYou) thankYou.hidden = false;
    }

    const controller = createNewsletterController({
      state,
      delay,
      now: dependencies.now,
      schedule: dependencies.schedule,
      cancelSchedule: dependencies.cancelSchedule,
      onOpen: showDialog,
      onClose: hideDialog,
      onSubscribed: showSubscribedState,
    });

    const onCloseClick = () => controller.close();
    const onLauncherClick = () => controller.open();
    const onBackdropClick = (event) => {
      if (event.target === backdrop) controller.close();
    };
    const onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && dialog && !dialog.hidden) {
        event.preventDefault();
        controller.close();
        return;
      }

      if (event.key !== 'Tab' || !dialog || dialog.hidden || !dialog.querySelectorAll) return;
      const focusable = Array.from(
        dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && documentRef.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && documentRef.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    if (closeButton) closeButton.addEventListener('click', onCloseClick);
    if (launcher) launcher.addEventListener('click', onLauncherClick);
    if (backdrop) backdrop.addEventListener('click', onBackdropClick);
    if (documentRef) documentRef.addEventListener('keydown', onDocumentKeydown);

    if (success) {
      controller.subscribe();
    } else if (state.read(dependencies.now ? dependencies.now() : Date.now()).subscribed) {
      showSubscribedState();
    } else {
      controller.start();
    }

    return {
      destroy() {
        controller.destroy();
        if (closeButton) closeButton.removeEventListener('click', onCloseClick);
        if (launcher) launcher.removeEventListener('click', onLauncherClick);
        if (backdrop) backdrop.removeEventListener('click', onBackdropClick);
        if (documentRef) documentRef.removeEventListener('keydown', onDocumentKeydown);
      },
    };
  }

  const api = { createNewsletterState, createNewsletterController, initNewsletter };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalScope) globalScope.WushuNewsletterState = api;

  if (globalScope && globalScope.document) {
    const initialize = () => {
      globalScope.document.querySelectorAll('[data-wushu-newsletter]').forEach((root) => {
        if (root.dataset.newsletterReady === 'true') return;
        root.dataset.newsletterReady = 'true';
        initNewsletter(root);
      });
    };

    if (globalScope.document.readyState === 'loading') {
      globalScope.document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }
    globalScope.document.addEventListener('shopify:section:load', initialize);
  }
})(typeof window !== 'undefined' ? window : globalThis);
